import React from 'react';
import { Box, Container, Typography, Paper, Link as MuiLink } from '@mui/material';
import { useNavigate } from 'react-router-dom';

const Terms = () => {
  const navigate = useNavigate();

  return (
    <Container component="main" maxWidth="md">
      <Box
        sx={{
          marginTop: 4,
          marginBottom: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box
          component="img"
          src="/StableTrack.png"
          alt="StableTrack"
          sx={{
            height: 100,
            width: 100,
            mb: 2,
            cursor: 'pointer',
          }}
          onClick={() => navigate('/')}
        />

        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Typography component="h1" variant="h4" align="center" sx={{ mb: 1 }}>
            Terms of Service
          </Typography>
          
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 4 }}>
            Effective Date: January 1, 2025 | Version 1.0
          </Typography>

          <Typography variant="body1" paragraph>
            Welcome to StableTrack. These Terms of Service ("Terms") govern your access to and use of StableTrack's veterinary practice management software and services (the "Service"). By creating an account or using our Service, you agree to be bound by these Terms.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
            1. Service Description
          </Typography>
          <Typography variant="body1" paragraph>
            StableTrack provides a comprehensive veterinary practice management system specifically designed for equine veterinarians. Our Service includes features for appointment scheduling, patient records management, clinical documentation, billing, inventory management, and reporting tools.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
            2. Acceptance of Terms
          </Typography>
          <Typography variant="body1" paragraph>
            By accessing or using StableTrack, you confirm that you are at least 18 years old and have the legal capacity to enter into these Terms. If you are using the Service on behalf of a veterinary practice or organization, you represent that you have the authority to bind that entity to these Terms.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
            3. Account Registration and Security
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>3.1 Account Creation:</strong> To use StableTrack, you must create an account by providing accurate and complete information, including your clinic name, email address, and other required details.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>3.2 Account Security:</strong> You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You must notify us immediately of any unauthorized access or security breach.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>3.3 Account Accuracy:</strong> You agree to keep your account information current and accurate at all times.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
            4. Subscription and Payment Terms
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>4.1 Subscription Plans:</strong> StableTrack offers various subscription tiers (Basic, Pro, Enterprise) with different features and limitations. Subscription details, pricing, and features are described on our pricing page.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>4.2 Payment Processing:</strong> All payments are processed securely through Stripe, our third-party payment processor. By subscribing to a paid plan, you authorize us to charge your designated payment method on a recurring basis.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>4.3 Billing Cycle:</strong> Subscription fees are billed in advance on a monthly or annual basis, depending on your selected plan. Fees are non-refundable except as required by law or as expressly stated in these Terms.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>4.4 Price Changes:</strong> We may change our subscription fees at any time. We will provide you with at least 30 days' notice of any price increase. Your continued use of the Service after the price change takes effect constitutes your acceptance of the new price.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
            5. Data Ownership and Usage Rights
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>5.1 Your Data:</strong> You retain all ownership rights to the data you enter into StableTrack, including patient records, client information, appointment data, and clinical notes ("Customer Data").
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>5.2 License to Use:</strong> You grant StableTrack a limited, non-exclusive license to host, store, process, display, and analyze your Customer Data for the purpose of providing, maintaining, and improving the Service, including the development and enhancement of AI-powered features and functionality.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
            6. Acceptable Use Policy
          </Typography>
          <Typography variant="body1" paragraph>
            You agree not to:
          </Typography>
          <Typography variant="body1" component="div">
            <ul style={{ marginTop: 0 }}>
              <li>Use the Service for any unlawful purpose or in violation of any applicable laws or regulations</li>
              <li>Attempt to gain unauthorized access to any part of the Service, other users' accounts, or our systems</li>
              <li>Interfere with or disrupt the Service or servers or networks connected to the Service</li>
              <li>Transmit any viruses, malware, or other malicious code</li>
              <li>Use the Service to store or transmit infringing, libelous, or otherwise unlawful content</li>
              <li>Reverse engineer, decompile, or attempt to extract the source code of the Service</li>
              <li>Resell, sublicense, or provide access to the Service to third parties without our written consent</li>
            </ul>
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
            7. Medical Records and Professional Responsibility
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>7.1 Professional Use:</strong> StableTrack is designed to assist veterinary professionals in managing their practice. You are solely responsible for the accuracy, completeness, and appropriateness of all medical records and clinical decisions made using the Service.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>7.2 No Medical Advice:</strong> StableTrack does not provide medical advice or veterinary services. Any AI-assisted features are tools to assist your professional judgment, not replace it.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>7.3 Compliance:</strong> You are responsible for ensuring your use of StableTrack complies with all applicable veterinary practice laws, regulations, and professional standards in your jurisdiction.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
            8. Service Availability and Support
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>8.1 Uptime:</strong> While we strive to maintain high availability, we do not guarantee that the Service will be uninterrupted or error-free. We may perform scheduled maintenance that temporarily interrupts service.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>8.2 Support:</strong> Support is provided via email and in-app feedback mechanisms. Response times and support levels vary by subscription tier.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>8.3 Updates:</strong> We may update, modify, or discontinue features of the Service at any time. We will make reasonable efforts to notify you of significant changes.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
            9. Limitation of Liability
          </Typography>
          <Typography variant="body1" paragraph>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, STABLETRACK SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, REVENUE, DATA, OR USE, ARISING OUT OF OR RELATED TO YOUR USE OF THE SERVICE, EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
          </Typography>
          <Typography variant="body1" paragraph>
            OUR TOTAL LIABILITY FOR ANY CLAIMS ARISING FROM OR RELATED TO THE SERVICE SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE 12 MONTHS PRECEDING THE CLAIM.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
            10. Disclaimers
          </Typography>
          <Typography variant="body1" paragraph>
            THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
            11. Termination
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>11.1 By You:</strong> You may cancel your subscription at any time through your account settings. Cancellation will take effect at the end of your current billing period.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>11.2 By Us:</strong> We may suspend or terminate your account if you violate these Terms, fail to pay fees when due, or for any other reason at our discretion with reasonable notice.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>11.3 Effect of Termination:</strong> Upon termination, your access to the Service will cease, and your Customer Data will be deleted after 90 days unless you export it or request an extension.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
            12. Indemnification
          </Typography>
          <Typography variant="body1" paragraph>
            You agree to indemnify and hold harmless StableTrack and its officers, directors, employees, and agents from any claims, damages, losses, liabilities, and expenses (including reasonable attorneys' fees) arising from your use of the Service, your violation of these Terms, or your violation of any rights of another party.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
            13. Changes to Terms
          </Typography>
          <Typography variant="body1" paragraph>
            We may modify these Terms at any time. We will notify you of material changes via email or through the Service. Your continued use of the Service after such notice constitutes your acceptance of the modified Terms.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
            14. Governing Law
          </Typography>
          <Typography variant="body1" paragraph>
            These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which StableTrack is registered, without regard to its conflict of law provisions.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
            15. Contact Information
          </Typography>
          <Typography variant="body1" paragraph>
            If you have any questions about these Terms, please contact us through our support channels within the application or via the feedback widget.
          </Typography>

          <Box sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: 'divider', textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              By using StableTrack, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
            </Typography>
            <Box sx={{ mt: 2 }}>
              <MuiLink
                component="button"
                variant="body2"
                onClick={() => navigate('/privacy')}
                sx={{ mr: 2 }}
              >
                Privacy Policy
              </MuiLink>
              <MuiLink
                component="button"
                variant="body2"
                onClick={() => navigate('/signup')}
              >
                Back to Signup
              </MuiLink>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Terms;
